import { Box, Stack, Typography } from '@mui/material';
import { cloneElement, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useWatch } from 'react-hook-form';

import {
  deriveSemanticTileKind,
  getIconForKind,
} from '@/components/diagrams/components/Tile/Tile.utils';
import { Button } from '@/components/form/baseInputs/Button';
import { ContextMenuButton } from '@/components/form/baseInputs/Button/ContextMenuButton';
import { EyeIcon } from '@/components/icons/EyeIcon';
import { LoadingOverlay } from '@/components/progress/LoadingOverlay/LoadingOverlay';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { useModalState } from '@/hooks/useModalState';
import { RegenerateBDSuggestionsV2MenuItem } from '@/modules/aiSuggestions/components/RegenerateBDSuggestionsV2MenuItem';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import { HypotheticalWaterfallScenarioCallout } from '@/modules/dispositiveProvisions/components/HypotheticalWaterfallScenarioCallout';
import { useOptionalDispositiveDiagramSyncContext } from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { deriveRecipientKindFromId } from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.helpers';
import { RecipientKind } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { PrimaryClientDeathSectionLabel } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/PrimaryClientDeathSectionLabel';
import { useDiagramNodeSelectionHandler } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/hooks/useDiagramNodeSelectionHandler';
import { usePrefetchRecipientDispositions } from '@/modules/dispositiveProvisions/hooks/usePrefetchRecipientDispositions';
import { EntityPreviewModal } from '@/modules/entities/components/EntityPreviewModal/EntityPreviewModal';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { useEditEntitySplitScreenModalContext } from '@/modules/entities/EditEntitySplitScreen/contexts/editEntitySplitScreenModal.context';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { EditEntitySplitScreenFooterRightActions } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenFooter';
import { EditEntitySplitScreenFormSectionSwitcher } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenFormSectionSwitcher';
import { EntityType } from '@/modules/entities/types/EntityType';
import { getEntityKindFromEntityType } from '@/modules/entities/utils/getEntityKindFromEntityType';
import { BdgPrimaryRecipient } from '@/modules/entityDiagram/bdgActiveTier';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import { pluralize } from '@/utils/formatting/strings';

import {
  BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE,
  BeneficiaryDesignationsFormShape,
} from './BeneficiaryDesignationsForm.types';
import { BeneficiaryDesignationTierSection } from './BeneficiaryDesignationTierSection';
import { BeneficiaryDesignationsFormProvider } from './contexts/beneficiaryDesignationsForm.context';
import { BeneficiaryDesignationSuggestionsProvider } from './contexts/beneficiaryDesignationSuggestions.context';
import { pickActiveBdgFormTier } from './hooks/pickActiveBdgFormTier';
import { useBeneficiaryDesignationRecipientOptions } from './hooks/useBeneficiaryDesignationRecipientOptions';
import { useSubmitBeneficiaryDesignations } from './hooks/useSubmitBeneficiaryDesignations';
import { useSyncBeneficiaryDesignationsDiagram } from './hooks/useSyncBeneficiaryDesignationsDiagram';

interface BeneficiaryDesignationsFormProps {
  initialFormValues: BeneficiaryDesignationsFormShape;
  onClose: () => void;
  onAfterSubmit: (afterSubmitSection?: EditEntitySection) => void;
  /** Called after a successful persist; see {@link useSubmitBeneficiaryDesignations}. */
  onDidSave?: () => void;
  isOnHypotheticalWaterfall: boolean;
  waterfallId?: string;
  waterfallDisplayName: string | null;
  entityType: EntityType;
  entitySubtypeId: string;
  /**
   * The retirement account's owner, used to default a spouse beneficiary to the
   * marital deduction recipient type independent of the diagram sync state.
   * Provided to the form via context. Null when there's no owner (insurance is
   * out of scope).
   */
  accountDecedentClientId: string | null;
  /** Override the entityId/displayName from EntityDetailsContext when the
   * form is rendered for a non-root target (e.g., a BDG-backed tile clicked
   * inside a DP form's diagram). */
  entityIdOverride?: string;
  displayNameOverride?: string;
  /**
   * Non-root only: parent DP diagram's active grantor death id. The sync
   * hook uses this for prefetch so the cascade matches the parent's
   * scenario instead of the BDG root's scenario.
   */
  firstDeathClientIdOverride?: string;
}

export function BeneficiaryDesignationsForm({
  initialFormValues,
  onClose,
  onAfterSubmit,
  onDidSave,
  isOnHypotheticalWaterfall,
  waterfallId,
  waterfallDisplayName,
  entityType,
  entitySubtypeId,
  accountDecedentClientId,
  entityIdOverride,
  displayNameOverride,
  firstDeathClientIdOverride,
}: BeneficiaryDesignationsFormProps) {
  const canEditDP = useCanEditDispositiveProvisions();
  const formMethods = useForm<BeneficiaryDesignationsFormShape>({
    defaultValues: initialFormValues,
  });

  const { entityId: contextEntityId, displayName: contextDisplayName } =
    useEntityDetailsContext();
  const entityId = entityIdOverride ?? contextEntityId;
  const displayName = displayNameOverride ?? contextDisplayName;
  const { footerRef, headerRef } = useEditEntitySplitScreenModalContext();

  // BDGs are only modeled for retirement and insurance personal accounts.
  // Fail loudly if a different entity type ever routes here so we don't
  // silently misclassify the mutation target.
  if (
    entityType !== 'retirement-account' &&
    entityType !== 'insurance-account'
  ) {
    throw new Error(
      `BeneficiaryDesignationsForm received unsupported entityType: ${entityType}`
    );
  }
  const bdgEntityType = entityType;

  const { onSubmit, isSubmitting, handleDiagramTileClickSubmission } =
    useSubmitBeneficiaryDesignations({
      entityId,
      entitySubtypeId,
      entityType: bdgEntityType,
      isOnHypotheticalWaterfall,
      waterfallId,
      initialFormValues,
      onAfterSubmit,
      onClose,
      onDidSave,
      handleSubmit: formMethods.handleSubmit,
    });

  // True when the BDG form is rendering the root entity (no override).
  // False when mounted for a clicked non-root BDG-backed tile; in that
  // case the sync hook must skip writing the root diagram overlay.
  const isEditingRootTarget = !entityIdOverride;

  const { primaryClients } = useHouseholdDetailsContext();

  // Clicking a diagram tile saves this form, then commits the clicked target
  // so the right-pane swap doesn't drop unsaved edits — same contract as the
  // DP form. Drives navigation off this BDG root/target too (not just DP).
  useDiagramNodeSelectionHandler({
    currentTargetId: entityId ?? '',
    isTwoClientHousehold: (primaryClients?.length ?? 0) > 1,
    isSubmitting,
    handleDiagramTileClickSubmission,
  });

  return (
    <FormProvider {...formMethods}>
      <BeneficiaryDesignationsFormProvider
        accountDecedentClientId={accountDecedentClientId}
      >
        <BeneficiaryDesignationsDiagramSync
          entityId={entityId}
          isEditingRootTarget={isEditingRootTarget}
          firstDeathClientIdOverride={firstDeathClientIdOverride}
          sourceIsRetirement={entityType === 'retirement-account'}
          isOnHypotheticalWaterfall={isOnHypotheticalWaterfall}
        />
        <HouseholdPermissionAwareDisabledProvider forceDisabled={!canEditDP}>
          <LoadingOverlay open={isSubmitting} />
          {/* Fetches the account's AI suggestions once and shares them across
            both tier matchers (each renders its own confirm card) and the
            header regenerate menu item (which refetches them after a manual
            regeneration). */}
          <BeneficiaryDesignationSuggestionsProvider entityId={entityId}>
            <Stack spacing={3} py={3}>
              {isOnHypotheticalWaterfall && waterfallDisplayName && (
                <Box px={3}>
                  <HypotheticalWaterfallScenarioCallout
                    waterfallName={waterfallDisplayName}
                    title="Hypothetical beneficiaries"
                    bodyPrefix="Beneficiaries for this entity"
                  />
                </Box>
              )}
              <BeneficiaryDesignationsHeader
                displayName={displayName ?? ''}
                entityType={entityType}
              />
              <PrimaryClientDeathSectionLabel label="Primary beneficiaries" />
              <BeneficiaryDesignationTierSection
                tier="primary"
                entityType={bdgEntityType}
                entityIdOverride={entityIdOverride}
                isOnHypotheticalWaterfall={isOnHypotheticalWaterfall}
              />
              <PrimaryClientDeathSectionLabel label="Contingent beneficiaries" />
              <BeneficiaryDesignationTierSection
                tier="contingent"
                entityType={bdgEntityType}
                entityIdOverride={entityIdOverride}
                isOnHypotheticalWaterfall={isOnHypotheticalWaterfall}
              />
            </Stack>
            {footerRef.current &&
              createPortal(
                <EditEntitySplitScreenFooterRightActions onSubmit={onSubmit} />,
                footerRef.current
              )}
            {/* The stable header shell renders the entity title and provides this
              slot; portal only the section switcher so the header doesn't blink
              out when the form remounts on a diagram tile click. */}
            {headerRef.current &&
              createPortal(
                <BeneficiaryDesignationsHeaderControls onSubmit={onSubmit} />,
                headerRef.current
              )}
          </BeneficiaryDesignationSuggestionsProvider>
        </HouseholdPermissionAwareDisabledProvider>
      </BeneficiaryDesignationsFormProvider>
    </FormProvider>
  );
}

// Mounts the BDG diagram sync hooks inside the form provider tree.
// `usePrefetchRecipientDispositions` flips `isLoadingRecipientDispositions`
// off; without it the split-screen diagram loader spins forever. We feed
// it the *currently active* tier (primary when the trigger client dies
// first, contingent when the alternate client dies first) under the
// currently selected death order so cascading recipient dispositions
// match the rendered scenario.
function BeneficiaryDesignationsDiagramSync({
  entityId,
  isEditingRootTarget,
  firstDeathClientIdOverride,
  sourceIsRetirement,
  isOnHypotheticalWaterfall,
}: {
  /** The edited entity id (`entityIdOverride ?? contextEntityId`); keys the
   * non-root override when this form is a non-root target. */
  entityId: string | null;
  isEditingRootTarget: boolean;
  firstDeathClientIdOverride?: string;
  /** Gates recipient look-through to retirement source accounts (T1-6171). */
  sourceIsRetirement: boolean;
  /** Must match the recipient typeahead so draft entities carry diagram
   * metadata and aren't dropped from the live preview. */
  isOnHypotheticalWaterfall: boolean;
}) {
  const { control } = useFormContext<BeneficiaryDesignationsFormShape>();
  const { primaryClients } = useHouseholdDetailsContext();
  // Key the options to the edited entity, not the diagram root, so a
  // root-entity recipient keeps its metadata in the tier check.
  const { metadataById } = useBeneficiaryDesignationRecipientOptions({
    entityIdOverride: entityId ?? undefined,
    isOnHypotheticalWaterfall,
  });
  const syncContext = useOptionalDispositiveDiagramSyncContext();
  const bdg = syncContext?.bdgDiagramSyncState ?? null;
  const triggerClientId =
    bdg?.defaultTriggerClientId ?? primaryClients?.[0]?.id ?? null;

  const primaryRecipients = useWatch({
    control,
    name: `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.primary.recipients`,
  });
  const contingentRecipients = useWatch({
    control,
    name: `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.contingent.recipients`,
  });

  // The death-order scenario this form's prefetch should align with. For
  // a non-root target the parent DP diagram drives the scenario, so the
  // override (its active grantor death) takes precedence over the BDG
  // sync state (which reflects the BDG root diagram only).
  const firstGrantorDeathId =
    firstDeathClientIdOverride ??
    bdg?.selectedPrimaryClientId ??
    triggerClientId;

  // Pick the active tier per current scenario via the same helper the
  // diagram-side resolver and sync hook use, so prefetch doesn't drift
  // from the cascade the diagram is actually rendering (e.g. for
  // survivorship policies where the primary tier is all primary clients,
  // the contingent tier is active).
  const primaryRecipientsForTierCheck = useMemo<BdgPrimaryRecipient[]>(
    () =>
      (primaryRecipients ?? []).map((r) => {
        if (!r.recipientId) {
          return { isSurvivingSpouseSentinel: false, individualId: null };
        }
        const kind = deriveRecipientKindFromId(r.recipientId);
        return {
          isSurvivingSpouseSentinel: kind === RecipientKind.SurvivingSpouse,
          individualId:
            kind === RecipientKind.Individual ? r.recipientId : null,
          lookThroughOwnerIds: sourceIsRetirement
            ? (metadataById[r.recipientId]?.lookThroughOwnerIds ?? null)
            : null,
        };
      }),
    [primaryRecipients, metadataById, sourceIsRetirement]
  );
  const possiblePrimaryClientIds = useMemo(
    () => primaryClients?.map((c) => c.id) ?? [],
    [primaryClients]
  );
  const activeTierRecipients = useMemo(() => {
    const { useContingent } = pickActiveBdgFormTier({
      isEditingRootTarget,
      primaryRecipientsForTierCheck,
      possiblePrimaryClientIds,
      firstGrantorDeathId,
      bdg,
    });
    return useContingent ? contingentRecipients : primaryRecipients;
  }, [
    bdg,
    contingentRecipients,
    firstGrantorDeathId,
    isEditingRootTarget,
    possiblePrimaryClientIds,
    primaryRecipients,
    primaryRecipientsForTierCheck,
  ]);

  // Derive the surviving-spouse id from the *active* first-death client so
  // it stays correct when the death-order toggle flips.
  const survivingSpouseRecipientId =
    primaryClients?.find((c) => c.id !== firstGrantorDeathId)?.id ?? null;

  usePrefetchRecipientDispositions({
    firstDeathRecipients: activeTierRecipients,
    firstGrantorDeathId,
    survivingSpouseRecipientId,
  });

  useSyncBeneficiaryDesignationsDiagram({
    entityId,
    primaryRecipients,
    contingentRecipients,
    isEditingRootTarget,
    sourceIsRetirement,
    isOnHypotheticalWaterfall,
  });
  return null;
}

interface BeneficiaryDesignationsHeaderProps {
  displayName: string;
  entityType: EntityType;
}

/**
 * Header for the beneficiary designations form showing the entity icon,
 * display name, and total beneficiary count. Mirrors the
 * DisposingObjectHeader pattern from DispositiveProvisionsFormV2.
 */
function BeneficiaryDesignationsHeader({
  displayName,
  entityType,
}: BeneficiaryDesignationsHeaderProps) {
  const { control } = useFormContext<BeneficiaryDesignationsFormShape>();

  const [primaryRecipients, contingentRecipients] = useWatch({
    control,
    name: [
      `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.primary.recipients`,
      `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.contingent.recipients`,
    ],
  });

  const numberOfBeneficiaries = useMemo(() => {
    return (
      (primaryRecipients?.length || 0) + (contingentRecipients?.length || 0)
    );
  }, [primaryRecipients, contingentRecipients]);

  const subtitleText = useMemo(() => {
    if (numberOfBeneficiaries === 0) {
      return 'No beneficiaries';
    }
    return pluralize({
      count: numberOfBeneficiaries,
      singular: 'beneficiary',
      plural: 'beneficiaries',
      includeCount: true,
    });
  }, [numberOfBeneficiaries]);

  const entityKind = getEntityKindFromEntityType(entityType);

  const tileIconElement = useMemo(() => {
    const node = {
      __typename: 'Entity' as const,
      entityKind,
      isCharitableOrganization: false,
    };
    const kind = deriveSemanticTileKind(node);
    const icon = getIconForKind(kind);
    return icon
      ? cloneElement(icon, {
          size: 20,
          color: COLORS.TEXT.SECONDARY,
          sx: { display: 'flex' },
        })
      : null;
  }, [entityKind]);

  return (
    <Stack direction="row" alignItems="center" spacing={2} px={2}>
      {tileIconElement && (
        <Stack
          padding="10px"
          bgcolor={COLORS.NAVY[100]}
          borderRadius="50%"
          justifyContent="center"
          alignItems="center"
        >
          {tileIconElement}
        </Stack>
      )}
      <Stack direction="column" spacing="2px">
        <Typography variant="title-s">{displayName}</Typography>
        <Typography variant="body-xs">{subtitleText}</Typography>
      </Stack>
    </Stack>
  );
}

interface BeneficiaryDesignationsHeaderControlsProps {
  onSubmit: () => void;
}

function BeneficiaryDesignationsHeaderControls({
  onSubmit,
}: BeneficiaryDesignationsHeaderControlsProps) {
  const { entityId } = useEntityDetailsContext();
  const { householdId } = useHouseholdDetailsContext();
  const canEditHouseholdData = useCanEditDispositiveProvisions();
  const [
    { isModalOpen: isPreviewModalOpen },
    { openModal: openPreviewModal, closeModal: closePreviewModal },
  ] = useModalState();

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {entityId && householdId && (
        <Button
          variant="secondary"
          size="sm"
          startIcon={EyeIcon}
          onClick={() => openPreviewModal()}
        >
          Preview deliverable
        </Button>
      )}
      {/* `onSubmit` saves the beneficiaries, then navigates to the picked
          section via `onAfterSubmit`, so a section switch persists first. */}
      <EditEntitySplitScreenFormSectionSwitcher onSubmit={onSubmit} />
      {/* Gate the whole menu here, not just inside the item: ContextMenuButton
          always renders its trigger button, so a null-returning item would
          leave an empty dropdown for read-only users. */}
      {entityId && householdId && canEditHouseholdData && (
        <ContextMenuButton
          data-testid="BeneficiaryDesignations-menu"
          size="xs"
          variant="transparent"
        >
          <RegenerateBDSuggestionsV2MenuItem entityId={entityId} />
        </ContextMenuButton>
      )}
      {isPreviewModalOpen && entityId && householdId && (
        <EntityPreviewModal
          entityId={entityId}
          householdId={householdId}
          onClose={closePreviewModal}
        />
      )}
    </Stack>
  );
}
